"use client"

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Search,
  Building,
  Briefcase,
  User,
  FileText,
  Target,
  Package,
  CheckSquare,
  FolderOpen,
  Receipt,
} from 'lucide-react'
import { get } from '../../utils/service'

interface Hit { id: string; title: string; subtitle?: string }

interface SearchResponse {
  success: boolean
  data: { query: string; results: Record<string, Hit[]> }
}

/** Result groups, in display order: label, icon and where a result opens. */
const GROUPS: Record<string, { label: string; icon: typeof Building; open?: (id: string) => string }> = {
  accounts: { label: 'Accounts', icon: Building, open: () => '/clients' },
  contacts: { label: 'Contacts', icon: User, open: () => '/contacts' },
  leads: { label: 'Leads', icon: Target, open: () => '/sales/leads' },
  deals: { label: 'Deals', icon: Briefcase, open: () => '/projects/all' },
  quotes: { label: 'Quotes', icon: FileText, open: (id) => `/quotes/${id}` },
  contracts: { label: 'Contracts', icon: FileText, open: (id) => `/contracts/${id}` },
  invoices: { label: 'Invoices', icon: Receipt, open: (id) => `/invoices/${id}` },
  projects: { label: 'Projects', icon: FolderOpen, open: () => '/delivery' },
  products: { label: 'Products & services', icon: Package, open: () => '/products' },
  tasks: { label: 'Tasks', icon: CheckSquare, open: () => '/tasks' },
}

const SearchResultsPage = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const query = searchParams.get('q') || ''

  const [results, setResults] = useState<Record<string, Hit[]>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults({})
      setLoading(false)
      return
    }
    let live = true
    setLoading(true)
    setError(null)
    get<SearchResponse>(`/search?q=${encodeURIComponent(query)}&limit=20`)
      .then((r) => live && setResults(r.data.results))
      .catch(() => live && setError('Search is unavailable right now. Please try again.'))
      .finally(() => live && setLoading(false))
    return () => { live = false }
  }, [query])

  const groups = Object.keys(GROUPS).filter((k) => results[k]?.length)
  const totalResults = groups.reduce((n, k) => n + results[k].length, 0)

  const SkeletonCard = () => (
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-3"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-32"></div>
    </div>
  )

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors text-xs"
          >
            <ArrowLeft className="w-3 h-3" />
            Back
          </button>

          <div className="flex items-center gap-3 mb-2">
            <Search className="w-4 h-4 text-gray-400" />
            <h1 className="text-sm font-semibold text-gray-900">Search Results</h1>
          </div>

          <p className="text-xs text-gray-600">
            {loading ? 'Searching...' : `${totalResults} result${totalResults !== 1 ? 's' : ''} for`}
            {!loading && <span className="font-medium text-gray-900"> "{query}"</span>}
          </p>
        </div>

        <div className="space-y-8">
          {loading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">{error}</p>
            </div>
          ) : totalResults === 0 ? (
            <div className="text-center py-8">
              <p className="text-xs text-gray-600">
                {query.trim().length < 2 ? 'Type at least 2 characters to search.' : <>No results found for "<span className="font-medium">{query}</span>"</>}
              </p>
            </div>
          ) : (
            groups.map((key) => {
              const group = GROUPS[key]
              const Icon = group.icon
              return (
                <section key={key}>
                  <h2 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500">{group.label}</h2>
                  {results[key].map((hit) => (
                    <motion.div
                      key={hit.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`pb-4 mb-4 border-b border-gray-100 last:border-0 last:mb-0 ${group.open ? 'cursor-pointer' : ''}`}
                      onClick={group.open ? () => navigate(group.open!(hit.id)) : undefined}
                    >
                      <div className="flex items-start gap-3">
                        <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="text-xs font-semibold text-gray-900 mb-1">{hit.title}</h3>
                          {hit.subtitle && <p className="text-xs text-gray-600">{hit.subtitle}</p>}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </section>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}

export default SearchResultsPage
